import { useEffect, useState } from "react";

import { useHistory, useLocation } from "react-router";

import { Button, Text } from "@alpacahq/ui/old";

import { Path } from "~/v2/path";
import { Layout } from "../../common/Layout";
import type { AcatsLocationState, EquityWithSymbol } from "../../common/types";
import { BackButton } from "../BackButton";
import { BalanceBox } from "./BalanceBox";
import { StocksBox } from "./StocksBox";

const PartialTransfer = () => {
  const history = useHistory();
  const { state: locationState } = useLocation<AcatsLocationState>();

  const [cashBalance, setCashBalance] = useState<string | undefined>(
    locationState?.cashBalance,
  );

  const [equities, setEquities] = useState<EquityWithSymbol[]>(
    locationState?.equities ?? [],
  );

  const onSaveCashBalance = (balance: string) => setCashBalance(balance);

  const onDeleteCashBalance = () => setCashBalance(undefined);

  const onSaveEquity = (equity: EquityWithSymbol) =>
    setEquities((equities) => {
      const isEdit = equities.some((e) => e.symbol === equity.symbol);
      return isEdit
        ? equities.map((e) => (e.symbol === equity.symbol ? equity : e))
        : [...equities, equity];
    });

  const onDeleteEquity = (equity: EquityWithSymbol) =>
    setEquities((equities) =>
      equities.filter((e) => e.symbol !== equity.symbol),
    );

  const onBackClicked = () =>
    history.replace({
      pathname: Path.ROUTE_PORTFOLIO_TRANSFER,
      state: undefined,
    });

  const onPreviousClicked = () => {
    history.replace({
      pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ASSETS_HOLDING,
      state: {
        ...locationState,
        equities,
        cashBalance,
      },
    });
  };

  const onContinueClicked = () => {
    // next page
    history.replace({
      pathname: Path.ROUTE_PORTFOLIO_TRANSFER_REVIEW,
      state: {
        ...locationState,
        cashBalance,
        equities,
      },
    });
  };

  useEffect(() => {
    if (locationState?.transferType !== "PARTIAL") {
      history.replace({
        pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ASSETS_HOLDING,
        state: locationState,
      });
    }
    if (!locationState?.taxId || !locationState?.accountNumber) {
      history.replace({
        pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ACCOUNT_INFO,
        state: locationState,
      });
    }
    if (!locationState?.brokerName || !locationState?.brokerNumber) {
      history.replace({
        pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ORIGINATING_BROKER,
        state: locationState,
      });
    }
  }, [locationState, history.replace]);

  return (
    <div>
      <BackButton
        onClick={onBackClicked}
        label="Back to Transfer Funds"
        className="mb-4"
      />
      <Layout title="Partial Portfolio Transfer" className="mt-2 pt-0">
        <div className="flex flex-col gap-6">
          <section className="flex w-full max-w-md flex-col gap-4">
            <h2 className="mb-0 p-0 font-medium text-gray-800 text-lg">
              Cash & Margin
            </h2>
            <Text className="text-gray-500 text-sm">
              Enter the amount of cash you'd like to transfer. This should match
              your brokerage account's “Available to Withdraw” amount.
            </Text>
            <BalanceBox
              title="Cash Balance"
              label="Specify Amount"
              onSave={onSaveCashBalance}
              onDelete={onDeleteCashBalance}
              defaultValue={cashBalance}
            />
          </section>
          <section className="flex w-full max-w-md flex-col gap-4">
            <h2 className="mb-0 p-0 font-medium text-gray-800 text-lg">
              Equity
            </h2>
            <StocksBox
              equities={equities}
              onSave={onSaveEquity}
              onDelete={onDeleteEquity}
            />
          </section>
          <div className="flex max-w-md items-center justify-between gap-2">
            <Button variant="v2/stroke" onClick={onPreviousClicked}>
              Previous
            </Button>
            <Button variant="v2/filled" onClick={onContinueClicked}>
              Continue
            </Button>
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default PartialTransfer;
